import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownAZ,
  EyeOff,
  Globe2,
  ArrowUpAZ,
  CheckCircle2,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MapPin,
  MapPinPlus,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import LoadFailureNote from "../components/LoadFailureNote";
import GroundEditPanel from "../components/GroundEditPanel";
import ArchiveToggle from "../components/ArchiveToggle";
import ConditionUpdate from "../components/ConditionUpdate";
import FacilityAddForm from "../components/FacilityAddForm";
import FacilityEditForm from "../components/FacilityEditForm";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PRIVATE_GROUND_LABEL, canPublishGround, isPrivateGround } from "../lib/groundEdit";
import { PLACE_TYPE_FILTERS, placeTypeLabel } from "../lib/placeType";
import { facilitiesByGround, facilitySurfaceLabel } from "../lib/facilities";
// Issue #690 — `GET /grounds` (this page's unbounded, `include_archived=1`
// read) now answers the SAME envelope shape the bbox modes do rather than a
// bare array; `readViewportGrounds` already reads either shape.
import { readViewportGrounds } from "../lib/mapViewport";
import {
  DEFAULT_VENUE_CRITERIA,
  VENUE_CONDITION_FILTERS,
  VENUE_FOCUS_PARAM,
  VENUE_PROVENANCE_FILTERS,
  VENUE_SORTS,
  VENUE_STATUS_FILTERS,
  VENUE_SURFACE_FILTERS,
  allVenuesSelected,
  applyVenueCriteria,
  auditActorLabel,
  auditRecordLabel,
  auditTimestampLabel,
  canConfirmBulkPublish,
  canConfirmImport,
  clearVenueFilters,
  describeAuditEntry,
  describeBulkPublishConfirm,
  describeBulkPublishResult,
  describeDuplicateWarning,
  describeImportBlock,
  describeImportMatch,
  describeImportMatchStatus,
  describeImportPreview,
  describeImportReason,
  describeImportResult,
  describeVenuePage,
  focusVenueCriteria,
  importStatusLabel,
  isVenueCriteriaNarrowed,
  pruneVenueSelection,
  selectVenues,
  selectableVenueIds,
  summariseBulkPublish,
  summariseImportPreview,
  summarisePreflight,
  toggleAllVenueSelection,
  toggleVenueSelection,
  venueRecordPath,
} from "../lib/adminVenues";

/**
 * `/admin/venues` — the Venues section of the one admin area (issue #166,
 * Martin's Q4 decision, 2026-08-27: "admins need a real edit/archive UI,
 * not curl"), the fourth section on the pattern #219 set:
 *
 *   /admin
 *   ├── Guides   <- AdminGuides.jsx (#219)
 *   ├── Users    <- AdminUsers.jsx (#226)
 *   ├── Grounds  <- AdminGroundDuplicates.jsx (#132 — the duplicate REPORT)
 *   └── Venues   <- here (#166 Q4 — the management console)
 *
 * --- THE SAME CONTROLS A CREATOR SEES, NOT AN ADMIN FORK OF THEM ---
 *
 * The edit form is `GroundEditPanel`, the archive control is
 * `ArchiveToggle`, the re-rate is `ConditionUpdate` — the exact components
 * the map's detail dialog renders for a creator, reused rather than
 * re-implemented (#497's rule: reuse is enforced, copy-paste drifts). What
 * differs is only WHO the gate lets through: `canOfferGroundEdit` now has
 * an admin tier (`isVenueAuthority`, Q3's one-predicate shape), and this
 * page sits behind the router's admin gate anyway. No authz is widened
 * here — the API's `checkGroundCorrectionGate` has accepted admins since
 * #131; this page is the affordance Q4 found missing.
 *
 * --- WHAT IT LISTS ---
 *
 * `GET /api/grounds?include_archived=1` — the same directory read every
 * signed-in user gets, with the FAC-11 opt-in. So this console shows an
 * admin exactly what the directory serves them and nothing more: other
 * people's PRIVATE pins are not in the read, which is Martin's no-backfill
 * decision (#129) holding here exactly as it holds on the duplicate report
 * one section over. An admin manages the shared directory, not other
 * people's drafts.
 *
 * --- FACILITIES: EDIT, ARCHIVE, AND DELETE ONLY WHERE THE API ALLOWS ---
 *
 * Delete is offered per facility and the API's answer is shown, not
 * paraphrased: a referenced surface comes back 409 `facility_in_use` with
 * guidance written to be read by the person who pressed the button
 * (archive instead — the sessions trained on it keep their history). The
 * confirm is a two-step inline control, `DismissControl`'s shape, because
 * a hard delete is the one irreversible verb on this page — everything
 * else here is a correction or a reversible archive.
 *
 * --- THE AUDIT TRAIL, ADMIN-ONLY (Q5) ---
 *
 * `GET /api/admin/grounds/:groundId/audit` — FAC-10's ledger, rendered
 * read-only under the selected venue: who changed what, when, including
 * deleted facilities and lifecycle rows. Q5 decided a record keeper does
 * NOT see their own record's trail; only this admin-gated page renders it,
 * and the route 403s anyone else regardless.
 */

/**
 * One venue in the scan list: what it is, where, and its states.
 *
 * Issue #613 — AND ITS ONE VERB, when that verb is publish. `canPublish`
 * is decided by the page on `canPublishGround`, the shared predicate (this
 * row does not re-derive it), and the control is `PublishControl` itself in
 * its row shape: the same call, the same two-step confirm, the same "there
 * is no unpublish". An admin scanning a filtered "Private (unpublished)"
 * list publishes from where the badge is, instead of pressing Manage and
 * hunting for a panel that renders below all 27 rows.
 *
 * `justPublished` is the row that has just changed under the filter it was
 * found by — see `keepIds` in `lib/adminVenues.js`.
 *
 * Issue #612 — AND THE CHECKBOX THAT PUTS IT IN A BATCH. It rides exactly
 * the same `canPublish` the row's own Publish button does, so a row can never
 * offer a way into a batch it cannot be published from, and it sits in the
 * publish strip rather than beside the name — see the strip's own comment for
 * why that placement is the one that does not cost the name its column.
 */
function VenueRow({ g, active, onManage, canPublish, justPublished, onPublished, selected, onToggleSelected }) {
  return (
    <Card
      className={`border-slate-200 ${active ? "ring-2 ring-[#0B2E59]" : ""}`}
      data-testid={`admin-venue-row-${g.ground_id}`}
    >
      <CardContent className="p-4 flex flex-wrap items-center gap-3">
        {/* #615 — the name owns its own LINE below `sm`, and that is the
            whole fix. `flex-1 min-w-0` let this block shrink towards zero,
            so the row never overflowed, `flex-wrap` never fired, and the
            badges and Manage kept their intrinsic width while the name was
            squeezed into a two-character column that `break-words` then
            dutifully broke mid-word ("B / el / l / Fi / el / d" on
            Martin's 360px Android). #613's "Just published" badge is what
            took the last of that column away.

            `basis-full` is a PROOF, not a mitigation: a flex item with
            `flex-basis: 100%` in a wrapping container is alone on its
            line, so the name's width is the row's full content box no
            matter how many badges the group beside it grows. A floor
            (`min-w-[10rem]`) would only move the tipping point.

            `sm:basis-auto sm:flex-1` restores the original single-line row
            from 640px up — desktop is byte-for-byte what it was. */}
        <div
          className="min-w-0 basis-full sm:basis-auto sm:flex-1"
          data-testid={`admin-venue-name-${g.ground_id}`}
        >
          <div className="font-display font-bold text-slate-900 text-sm break-words">{g.name}</div>
          <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
            <MapPin size={12} className="shrink-0" />
            <span className="break-words">{[g.city, g.country].filter(Boolean).join(", ") || "—"}</span>
          </div>
        </div>
        {/* Every badge this row can show lives in HERE, and the test for
            #615 pins that: a badge added as a sibling of the name would be
            back on the name's line. `min-w-0` so the group yields rather
            than forcing the row wider than the viewport. */}
        <div className="flex flex-wrap items-center gap-1.5 min-w-0" data-testid={`admin-venue-badges-${g.ground_id}`}>
          <Badge variant="outline" className="text-[10px] font-medium">
            {placeTypeLabel(g.place_type) || "—"}
          </Badge>
          {g.is_verified && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
              <ShieldCheck size={12} /> Verified
            </span>
          )}
          {g.is_global && !g.is_verified && (
            <span className="text-[10px] font-semibold text-[#0B2E59]">Global</span>
          )}
          {/* Issue #606 — THE BADGE THAT WAS MISSING. A ground with neither
              flag is served to its creator alone, so an admin scanning this
              list saw 27 of his own venues sitting among the published ones
              with nothing to tell them apart. Amber and worded as a
              consequence ("only you can see this"), not as a status word:
              "Private" alone reads as a setting somebody chose. */}
          {isPrivateGround(g) && (
            <span
              className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
              data-testid={`admin-venue-private-${g.ground_id}`}
            >
              <EyeOff size={12} /> {PRIVATE_GROUND_LABEL}
            </span>
          )}
          {/* #613 — this row was published a moment ago and is still on
              screen under the filter that found it. Said plainly, because
              a row that stays in a "Private (unpublished)" list after it
              stops being private otherwise reads as a failed publish. */}
          {justPublished && (
            <span
              className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800"
              data-testid={`admin-venue-just-published-${g.ground_id}`}
            >
              <Globe2 size={12} /> Just published
            </span>
          )}
          {g.archived_at && (
            <Badge className="text-[10px] bg-slate-200 text-slate-600 hover:bg-slate-200">Archived</Badge>
          )}
        </div>
        <Button
          type="button"
          variant={active ? "default" : "outline"}
          size="sm"
          className="text-xs"
          style={active ? { background: "#0B2E59" } : undefined}
          onClick={onManage}
          data-testid={`admin-venue-manage-${g.ground_id}`}
        >
          {active ? "Managing" : "Manage"}
        </Button>
        {/* THE PUBLISH STRIP. Its own line at every width (`basis-full`),
            never a fourth column squeezed beside the badges — the confirm
            step expands to a sentence, and a row that has to fit one at
            390px is a row that overflows sideways.

            #612's select checkbox lives HERE, and the placement is the whole
            of the layout decision. Every other candidate spot — before the
            name, after Manage — is an item on the row's MAIN line, and from
            640px up the name is `sm:flex-1`: it absorbs whatever the other
            items leave. #621 measured that column at 150px on a 768 tablet
            with a floor of 110 (`MIN_VENUE_NAME_WIDTH`), so a checkbox and
            its gap would have spent a quarter of the remaining margin to
            gain nothing. On this line it competes with nothing, and it is
            beside the verb it arms rather than across the row from it. */}
        {canPublish && (
          <div
            className="basis-full flex flex-wrap items-center gap-2 min-w-0"
            data-testid={`admin-venue-publish-strip-${g.ground_id}`}
          >
            {onToggleSelected && (
              <label
                className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer"
                data-testid={`admin-venue-select-label-${g.ground_id}`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 accent-[#0B2E59]"
                  checked={Boolean(selected)}
                  onChange={onToggleSelected}
                  aria-label={`Select ${g.name} to publish with others`}
                  data-testid={`admin-venue-select-${g.ground_id}`}
                />
                Select
              </label>
            )}
            <PublishControl ground={g} onPublished={onPublished} variant="row" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The delete verb, closed until pressed — `DismissControl`'s two-step
 * shape, because unlike the archive beside it this one is irreversible.
 * The API's refusal (409 `facility_in_use` + the session count + "archive
 * it instead") is rendered verbatim; it is written for this reader.
 */
function FacilityDeleteControl({ groundId, facility, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function remove() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/grounds/${groundId}/facilities/${facility.facility_id}`);
      onDeleted();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2">
      {!confirming ? (
        <button
          type="button"
          onClick={() => { setError(""); setConfirming(true); }}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 min-h-11 text-[11px] font-semibold text-red-700 transition hover:border-red-300"
          data-testid={`admin-facility-delete-${facility.facility_id}`}
        >
          <Trash2 size={12} /> Delete this surface
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-600">
            Delete for good? Only possible while no training session references it.
          </span>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="text-xs"
            disabled={busy}
            onClick={remove}
            data-testid={`admin-facility-delete-confirm-${facility.facility_id}`}
          >
            {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
            Delete
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs"
            disabled={busy}
            onClick={() => setConfirming(false)}
            data-testid={`admin-facility-delete-cancel-${facility.facility_id}`}
          >
            Cancel
          </Button>
        </div>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert" data-testid={`admin-facility-delete-error-${facility.facility_id}`}>
          {error}
        </p>
      )}
    </div>
  );
}

/** The selected venue's surfaces: re-rate, archive/restore, delete — the
 * same per-facility controls the map's detail dialog gives a creator, plus
 * the delete only this console offers. */
function AdminFacilityRows({ ground, list, onChanged }) {
  const rows = (list || []).filter(Boolean);
  if (rows.length === 0) {
    return (
      <>
        <p className="text-xs text-slate-500 mt-2" data-testid="admin-venue-no-facilities">
          No surfaces are recorded at this venue.
        </p>
        {/* #542 — the same add control the map's detail dialog offers,
            through the same shared component (#497's rule: reuse, not a
            fork). This page sits behind the router's admin gate and the
            API's `checkGroundCorrectionGate` admits admins on any standing
            record, so no client-side predicate is needed here — parity
            with the unconditional GroundEditPanel above. `onChanged`
            refetches (this page's rule: server-owned state, no spliced
            local arrays), which also lands the new row in the audit trail
            below. */}
        <FacilityAddForm ground={ground} onAdded={onChanged} testId={`admin-facility-add-${ground.ground_id}`} />
      </>
    );
  }
  return (
    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="admin-venue-facilities">
      {rows.map((f) => (
        <div key={f.facility_id} className="rounded-md border border-slate-200 p-2.5" data-testid={`admin-facility-row-${f.facility_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-sm text-slate-900 truncate">{f.name}</div>
            {f.archived_at && (
              <Badge className="text-[10px] bg-slate-200 text-slate-600 hover:bg-slate-200 shrink-0">Archived</Badge>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{facilitySurfaceLabel(f.surface)}</div>
          {/* #601 — the rename Martin needs after the #580 import: an edit
              in place, so the surface keeps its id and with it its ratings,
              its history and every training session pointing at it. Same
              shared component the map's detail dialog renders (#497: reuse,
              not a fork), and unconditional here for the same reason the
              GroundEditPanel above and the ConditionUpdate below are — this
              page is behind the router's admin gate, and the API's
              `checkGroundCorrectionGate` admits an admin on any standing
              record, so the client-side predicate the map needs
              (`canEditFacility`) is already satisfied for everyone who can
              see this list. */}
          <FacilityEditForm
            ground={ground}
            facility={f}
            onSaved={onChanged}
            testId={`admin-facility-edit-${f.facility_id}`}
          />
          <ConditionUpdate
            target={{ groundId: ground.ground_id, facilityId: f.facility_id }}
            condition={f.condition}
            ratedAt={f.condition_rated_at}
            canEdit
            label="Condition"
            testId={`admin-facility-condition-${f.facility_id}`}
            onUpdated={onChanged}
          />
          <ArchiveToggle
            basePath={`/grounds/${ground.ground_id}/facilities/${f.facility_id}`}
            archived={Boolean(f.archived_at)}
            noun="surface"
            onDone={onChanged}
            testId={`admin-facility-archive-${f.facility_id}`}
          />
          <FacilityDeleteControl groundId={ground.ground_id} facility={f} onDeleted={onChanged} />
        </div>
      ))}
      {/* #542 — see the empty branch above; a venue with three nets gets a
          fourth through the same control. Inside the grid so it flows as
          the next cell. */}
      <div>
        <FacilityAddForm ground={ground} onAdded={onChanged} testId={`admin-facility-add-${ground.ground_id}`} />
      </div>
    </div>
  );
}

/**
 * Issue #606 — PUBLISH THIS VENUE. The action Martin needs on 27 rows he
 * has already typed.
 *
 * TWO STEPS, like `FacilityDeleteControl` above and for the same reason:
 * this is the one verb on this page that cannot be taken back. Publishing
 * puts the record in front of everybody, and other people start bookmarking
 * it and logging matches at it immediately; there is no unpublish (see
 * `setGroundPublished`), only archive or merge, both of which leave the row
 * resolvable. The confirm line says so rather than leaving it to be
 * discovered.
 *
 * WHAT IT DOES NOT DO is decide who may press it — `canPublishGround` does,
 * on the shared predicate every venue affordance rides, and the API decides
 * again behind an admin-gated router. A refusal is rendered verbatim: the
 * two 409s (merged, archived) are written for this reader.
 *
 * --- TWO PLACES, ONE CONTROL (issue #613) ---
 *
 * Martin could not reach this on a phone. It renders inside the detail
 * card, and that card is a sibling AFTER the whole row list — on a 390px
 * viewport with 27 private venues, pressing "Manage" on the first row
 * opened the publish button about 6000px below the finger that pressed it.
 * The button said "Managing" and nothing else appeared to happen.
 *
 * The fix is to also offer publish ON THE ROW, beside the badge that says
 * the venue is private — an admin working a filtered "Private
 * (unpublished)" list publishes from where the badge is. `variant="row"`
 * is that placement, and it is the SAME component: one `api.post`, one
 * two-step confirm, one "there is no unpublish" sentence. A second publish
 * client is exactly how the two would come to disagree (#497).
 *
 * What the row variant drops is only the framing the row already carries:
 * the amber panel repeats `PRIVATE_GROUND_LABEL`, which is the badge
 * sitting next to it, and the "not in the shared directory" explainer,
 * whose one irreversible fact — "there is no unpublish" — is in the
 * confirm step that has to be passed either way.
 *
 * --- ISSUE #612: THE DUPLICATE CHECK, ON THE WAY IN ---
 *
 * `findDuplicateGrounds` has always run on the CREATE form and on the CSV
 * import preview, and never on the promotion of a venue that already exists —
 * which is the one publish path Martin uses. The risk council data carries is
 * not accuracy, it is the same park sitting in his hand-entered list, in the
 * 74 OSM rows, and possibly in the directory already under a slightly
 * different name. Once it is on the shared shelf everyone inherits the mess
 * and there is no delete, only merge or archive.
 *
 * So arming the confirm fires `POST /admin/grounds/publish/preflight` for
 * this one venue — the same endpoint, and therefore the same matcher, the
 * batch below uses — and the matches are rendered inside the confirm.
 *
 * IT WARNS, IT NEVER BLOCKS, and the code says so three times over: the
 * Publish button's `disabled` reads `busy` and nothing else, so it is
 * pressable while the check is still in flight, after the check has found ten
 * matches, and after the check has failed outright. #612 is explicit that the
 * admin is the reviewer and may legitimately publish a near-name match; a
 * check that could stop the write would be a block wearing a warning's copy.
 * The rule is asserted in `lib/adminVenues.test.js` as `canConfirmBulkPublish`
 * — a function whose only input is how many venues are selected.
 */
function PublishControl({ ground, onPublished, variant = "panel" }) {
  const compact = variant === "row";
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preflight, setPreflight] = useState(null);
  const [preflightFailed, setPreflightFailed] = useState(false);
  const [checking, setChecking] = useState(false);

  /** Open the confirm AND ask what this venue might duplicate. The two happen
   * together rather than the confirm waiting on the answer: the reader can
   * read the one-way sentence while the check runs, and can press through it
   * if the check never comes back. */
  function arm() {
    setError("");
    setConfirming(true);
    setPreflight(null);
    setPreflightFailed(false);
    setChecking(true);
    api.post("/admin/grounds/publish/preflight", { ground_ids: [ground.ground_id] })
      .then((r) => setPreflight(r.data))
      .catch(() => setPreflightFailed(true))
      .finally(() => setChecking(false));
  }

  const warning = preflight || preflightFailed
    ? summarisePreflight(preflight, { failed: preflightFailed })
    : null;
  const matches = warning && !warning.failed ? warning.withDuplicates[0]?.duplicates || [] : [];

  async function publish() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/admin/grounds/${ground.ground_id}/publish`);
      onPublished();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not publish this venue");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  // The row variant's testids are their own namespace, because a private
  // row that is ALSO the selected one renders both controls at once — two
  // elements sharing a testid is a test that passes on the wrong node.
  return (
    <div
      className={compact
        ? "flex flex-wrap items-center gap-2 min-w-0"
        : "mt-3 rounded-md border border-amber-200 bg-amber-50 p-3"}
      data-testid={compact ? `admin-venue-row-publish-control-${ground.ground_id}` : "admin-venue-publish"}
    >
      {!compact && (
        <>
          <p className="text-xs font-semibold text-amber-900">
            <EyeOff size={12} className="inline mr-1 -mt-0.5" />
            {PRIVATE_GROUND_LABEL}
          </p>
          <p className="text-[11px] text-amber-800 mt-0.5">
            This venue is not in the shared directory. Publishing adds it — and its surfaces — for every
            signed-in user, and cannot be undone.
          </p>
        </>
      )}
      {!confirming ? (
        compact ? (
          <Button
            type="button"
            size="sm"
            className="text-xs"
            style={{ background: "#0B2E59" }}
            onClick={arm}
            data-testid={`admin-venue-row-publish-${ground.ground_id}`}
          >
            <Globe2 size={12} className="mr-1" /> Publish
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className="mt-2 text-xs"
            style={{ background: "#0B2E59" }}
            onClick={arm}
            data-testid={`admin-venue-publish-${ground.ground_id}`}
          >
            <Globe2 size={12} className="mr-1" /> Publish to the shared directory
          </Button>
        )
      ) : (
        <div className={compact
          ? "flex flex-wrap items-center gap-2 min-w-0"
          : "mt-2 flex flex-wrap items-center gap-2"}>
          <span className="text-[11px] text-amber-900 break-words min-w-0">
            Publish “{ground.name}”? Everyone will see it, and there is no unpublish.
          </span>
          {/* #612 — the warning, never a gate. Nothing here touches the
              Publish button's `disabled`. */}
          {checking && (
            <span
              className="inline-flex items-center gap-1 text-[11px] text-slate-500"
              data-testid={`admin-venue-duplicate-checking-${ground.ground_id}`}
            >
              <Loader2 size={12} className="animate-spin" /> Checking for duplicates…
            </span>
          )}
          {warning && warning.failed && (
            <span
              className="text-[11px] text-slate-600 break-words min-w-0"
              data-testid={`admin-venue-duplicate-unchecked-${ground.ground_id}`}
            >
              {describeDuplicateWarning(warning)}
            </span>
          )}
          {matches.length > 0 && (
            <span
              className="basis-full text-[11px] text-amber-900 break-words min-w-0"
              data-testid={`admin-venue-duplicate-warning-${ground.ground_id}`}
            >
              <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
              Already in the directory, or very like it: {matches.map((d) => d.name).join(", ")}. Publish
              anyway if this is a different place.
            </span>
          )}
          <Button
            type="button"
            size="sm"
            className="text-xs"
            style={{ background: "#0B2E59" }}
            disabled={busy}
            onClick={publish}
            data-testid={compact
              ? `admin-venue-row-publish-confirm-${ground.ground_id}`
              : `admin-venue-publish-confirm-${ground.ground_id}`}
          >
            {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
            Publish
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs"
            disabled={busy}
            onClick={() => setConfirming(false)}
            data-testid={compact
              ? `admin-venue-row-publish-cancel-${ground.ground_id}`
              : `admin-venue-publish-cancel-${ground.ground_id}`}
          >
            Cancel
          </Button>
        </div>
      )}
      {error && (
        <p
          className="mt-1 text-xs text-red-600 break-words min-w-0"
          role="alert"
          data-testid={compact
            ? `admin-venue-row-publish-error-${ground.ground_id}`
            : "admin-venue-publish-error"}
        >
          {error}
        </p>
      )}
    </div>
  );
}

/** FAC-10's ledger, read-only (Q5: admins only — this page is the only
 * renderer, and the route 403s everyone else regardless). */
function AuditTrail({ entries, failed, onRetry }) {
  return (
    <div className="mt-5" data-testid="admin-venue-audit">
      <h3 className="font-display font-bold text-sm text-slate-900">Change history</h3>
      <p className="text-xs text-slate-500 mt-0.5">
        Every recorded change to this venue and its surfaces, newest first. Visible to admins only.
      </p>
      {failed && <LoadFailureNote what="the change history" onRetry={onRetry} testId="admin-venue-audit-failed" />}
      {!failed && entries && entries.length === 0 && (
        <p className="text-xs text-slate-500 mt-2" data-testid="admin-venue-audit-empty">
          Nothing has been recorded yet — the ledger starts with #500; older changes predate it.
        </p>
      )}
      {!failed && entries && entries.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="rounded-md border border-slate-200 p-2 text-xs text-slate-700"
              data-testid="admin-venue-audit-entry"
            >
              <span className="font-semibold">{auditRecordLabel(entry)}</span>{" "}
              <span className="break-words">{describeAuditEntry(entry)}</span>
              <span className="block text-[11px] text-slate-500 mt-0.5">
                {auditTimestampLabel(entry.created_at)} · by {auditActorLabel(entry)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One row of the dry-run verdict: a status pill plus its reason. Colours
 * follow the three outcomes — create (navy), matches-existing (amber),
 * rejected (red) — so an admin reads the shape of the import at a glance. */
function StatusPill({ status }) {
  // Colour only — the WORDS come from `importStatusLabel` (#627), so the
  // verdict vocabulary is asserted in `lib/adminVenues.test.js` rather than
  // promised in JSX. `check_failed` and `write_failed` are amber and red
  // respectively: one is "we do not know", the other is "it did not land".
  const cls =
    {
      will_create: "bg-[#0B2E59] text-white",
      matches_existing: "bg-amber-100 text-amber-800",
      rejected: "bg-red-100 text-red-700",
      check_failed: "bg-amber-100 text-amber-900 border border-amber-400",
      write_failed: "bg-red-100 text-red-700 border border-red-400",
      skipped: "bg-slate-100 text-slate-500",
    }[status] || "bg-slate-100 text-slate-500";
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>
      {importStatusLabel(status)}
    </span>
  );
}

/**
 * Issue #580 — the bulk-importer panel: upload a grounds CSV and an optional
 * fields/nets CSV, PREVIEW (a dry run that writes nothing), read the per-row
 * verdict, then CONFIRM to publish. The two-step is the safety contract, and
 * it is the API's too: preview hits `/import/preview` (no writes), confirm
 * hits `/import/confirm` (the only path that sets `is_global=true`). Nothing
 * reaches the shared directory until a human presses Confirm here.
 *
 * The Confirm button stays disabled until there is a clean preview with at
 * least one will-create row AND a `source` typed — provenance is required to
 * publish (migration 0057), so the control refuses to arm without it.
 */
function ImportPanel({ onImported }) {
  const [groundsCsv, setGroundsCsv] = useState("");
  const [facilitiesCsv, setFacilitiesCsv] = useState("");
  const [source, setSource] = useState("");
  const [sourceLicence, setSourceLicence] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function readFile(file, setter) {
    setPreview(null);
    setResult(null);
    if (!file) { setter(""); return; }
    setter(await file.text());
  }

  async function runPreview() {
    if (busy || !groundsCsv.trim()) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await api.post("/admin/grounds/import/preview", {
        grounds_csv: groundsCsv,
        facilities_csv: facilitiesCsv || null,
      });
      setPreview(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  }

  async function runConfirm() {
    if (busy || !canConfirm) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.post("/admin/grounds/import/confirm", {
        grounds_csv: groundsCsv,
        facilities_csv: facilitiesCsv || null,
        source,
        source_licence: sourceLicence || null,
      });
      setResult(res.data);
      setPreview(null);
      onImported();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  }

  const fileErrors = preview?.file_errors || [];
  const summary = preview ? summariseImportPreview(preview) : null;
  // #627 — the gate and the reason it is shut both come from the lib, so the
  // rule is asserted in `lib/adminVenues.test.js` and the button can never
  // arm on a file `confirm` would refuse (`rows_unchecked`).
  const canConfirm = canConfirmImport({ preview, source });
  const blockedBecause = describeImportBlock({ preview, source });

  return (
    <Card className="border-slate-200" data-testid="admin-venue-import">
      <CardContent className="p-5 space-y-4">
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
            <Upload size={16} /> Bulk import
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Upload a grounds CSV and, optionally, a fields &amp; nets CSV. Preview is a dry run — nothing is
            published until you press Confirm. Imported venues land in the shared directory, unverified, stamped
            with the source you name below. A file with more rows than one import allows is refused whole, with
            the limit in the message — never half-imported, because nothing deletes a ground.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs font-semibold text-slate-700">
            Grounds CSV
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => readFile(e.target.files?.[0], setGroundsCsv)}
              className="mt-1 block w-full text-xs"
              data-testid="admin-import-grounds-file"
            />
          </label>
          <label className="text-xs font-semibold text-slate-700">
            Fields &amp; nets CSV (optional)
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => readFile(e.target.files?.[0], setFacilitiesCsv)}
              className="mt-1 block w-full text-xs"
              data-testid="admin-import-facilities-file"
            />
          </label>
          {/* `htmlFor`/`id` rather than nesting alone: `Input` is a component,
              so the a11y lint cannot see the control inside the label and a
              screen reader relying on the accessible name gets it from the
              explicit pairing either way. */}
          <label className="text-xs font-semibold text-slate-700" htmlFor="admin-import-source">
            Source (required to confirm)
            <Input
              id="admin-import-source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g. auckland-council-parks-2026-09"
              className="mt-1"
              data-testid="admin-import-source"
            />
          </label>
          <label className="text-xs font-semibold text-slate-700" htmlFor="admin-import-source-licence">
            Source licence (optional)
            <Input
              id="admin-import-source-licence"
              value={sourceLicence}
              onChange={(e) => setSourceLicence(e.target.value)}
              placeholder="e.g. CC-BY-4.0"
              className="mt-1"
              data-testid="admin-import-source-licence"
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            disabled={busy || !groundsCsv.trim()}
            onClick={runPreview}
            data-testid="admin-import-preview"
          >
            {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
            Preview
          </Button>
          <Button
            type="button"
            size="sm"
            className="text-xs"
            style={{ background: canConfirm ? "#0B2E59" : undefined }}
            disabled={busy || !canConfirm}
            onClick={runConfirm}
            data-testid="admin-import-confirm"
          >
            Confirm import
          </Button>
          {blockedBecause && (
            <span className="text-[11px] text-slate-500" data-testid="admin-import-blocked">
              {blockedBecause}
            </span>
          )}
        </div>

        {error && (
          <p className="text-xs text-red-600" role="alert" data-testid="admin-import-error">
            {error}
          </p>
        )}

        {fileErrors.length > 0 && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3" data-testid="admin-import-file-errors">
            <p className="text-xs font-semibold text-red-700 flex items-center gap-1">
              <AlertTriangle size={12} /> The file cannot be imported:
            </p>
            <ul className="mt-1 list-disc pl-5 text-[11px] text-red-700">
              {fileErrors.map((e) => (
                <li key={e} title={e}>
                  {describeImportReason(e)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {summary && (
          <div className="text-xs text-slate-700 space-y-2" data-testid="admin-import-preview-summary">
            <p className="font-semibold">{describeImportPreview(summary)}</p>
            <div className="space-y-1">
              {preview.grounds.map((g) => (
                <div key={`${g.ref}-${g.line}`} className="rounded border border-slate-200 p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill status={g.status} />
                    {/* Issue #645 — the row number, on EVERY row, not only a
                        blank-name one: `line` is how an admin finds the row
                        in their own file, and a rejection for a long name
                        (which still has a name) used to have no number at
                        all. */}
                    <span className="text-slate-400 text-[11px]" data-testid={`admin-import-line-${g.line}`}>
                      Line {g.line}
                    </span>
                    <span className="font-semibold text-slate-900">{g.name || "(no name)"}</span>
                    <span className="text-slate-400">{placeTypeLabel(g.place_type) || g.place_type}</span>
                    {g.reason && (
                      <span className={g.status === "check_failed" ? "text-amber-800" : "text-red-600"} title={g.reason}>
                        {describeImportReason(g.reason, g)}
                      </span>
                    )}
                  </div>
                  {/* Issue #647 — a `matches_existing` row names what it
                      matched, in words, and links to it: a coloured pill
                      told an admin nothing about whether editing the name
                      would fix the row (it would not — the ground is
                      already there) or would just make the same venue
                      publishable a second time. */}
                  {g.status === "matches_existing" && g.matches?.length > 0 && (
                    <div className="mt-1 pl-1 text-[11px] text-amber-800" data-testid={`admin-import-matches-${g.line}`}>
                      <p>{describeImportMatchStatus()}</p>
                      <ul className="mt-0.5 list-disc pl-4">
                        {g.matches.map((m) => (
                          <li key={m.ground_id}>
                            <Link
                              to={venueRecordPath(m.ground_id)}
                              className="underline decoration-amber-400 underline-offset-2 hover:decoration-amber-700"
                              data-testid={`admin-import-match-${m.ground_id}`}
                            >
                              {describeImportMatch(m)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {g.facilities?.length > 0 && (
                    <ul className="mt-1 pl-4 text-[11px] text-slate-600">
                      {g.facilities.map((f) => (
                        <li key={`${f.line}-${f.name}`} className="flex items-center gap-1.5">
                          <StatusPill status={f.status} />
                          <span className="text-slate-400">Line {f.line}</span>
                          <span>
                            {f.name} ({f.kind || "?"})
                          </span>
                          {f.reason && (
                            <span className="text-red-600" title={f.reason}>
                              {describeImportReason(f.reason, f)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            {preview.orphan_facilities?.length > 0 && (
              <div data-testid="admin-import-orphans">
                <p className="font-semibold text-red-700">
                  Facilities with no matching ground ({preview.orphan_facilities.length}):
                </p>
                <ul className="pl-5 list-disc text-[11px] text-red-700">
                  {preview.orphan_facilities.map((f) => (
                    <li key={`${f.line}-${f.name}`} title={f.reason}>
                      Line {f.line}: {f.name} → {describeImportReason(f.reason, f)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {result && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800"
            data-testid="admin-import-result"
          >
            <p className="font-semibold flex items-center gap-1">
              <CheckCircle2 size={12} /> {describeImportResult(result)} Stamped “{result.source}”.
            </p>
            {/* #627 — a partial failure NAMES its rows. #612's rule: the
                report is the result, so a write that did not land is on
                screen next to the ones that did, never folded into a count
                the admin has to notice. */}
            {result.grounds?.some((g) => g.status === "write_failed") && (
              <ul className="mt-1 list-disc pl-5 text-red-700" data-testid="admin-import-write-failures">
                {result.grounds
                  .filter((g) => g.status === "write_failed")
                  .map((g) => (
                    <li key={`${g.ref}-${g.line}`}>
                      Line {g.line} — {g.name}: {describeImportReason(g.reason, g)}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Issue #603 — one dropdown per question an admin actually asks of this
 * list, in a strip above it. Every option list and every predicate lives in
 * `lib/adminVenues.js`; this component only draws them, which is the split
 * that makes the rules assertable at all (no component is mounted in this
 * repo's unit tests).
 *
 * "Clear filters" appears only when something is narrowing the list —
 * `isVenueCriteriaNarrowed` — so it cannot sit there implying a filter is
 * on when none is. It clears the FILTERS and the search, not the sort:
 * an ordering is not a thing you are stuck behind.
 */
function VenueFilters({ criteria, onChange, onClear }) {
  const narrowed = isVenueCriteriaNarrowed(criteria);
  const sortMeta = VENUE_SORTS.find((s) => s.value === criteria.sort) || VENUE_SORTS[0];
  const SortIcon = criteria.direction === "desc" ? ArrowDownAZ : ArrowUpAZ;
  const selects = [
    { key: "placeType", options: PLACE_TYPE_FILTERS, testId: "admin-venues-filter-type" },
    { key: "status", options: VENUE_STATUS_FILTERS, testId: "admin-venues-filter-status" },
    { key: "surfaces", options: VENUE_SURFACE_FILTERS, testId: "admin-venues-filter-surfaces" },
    { key: "condition", options: VENUE_CONDITION_FILTERS, testId: "admin-venues-filter-condition" },
    { key: "provenance", options: VENUE_PROVENANCE_FILTERS, testId: "admin-venues-filter-provenance" },
  ];

  return (
    <div className="space-y-3" data-testid="admin-venues-controls">
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={criteria.query}
          onChange={(e) => onChange({ query: e.target.value })}
          placeholder="Search by name, city or country"
          className="pl-9"
          data-testid="admin-venues-search"
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {selects.map(({ key, options, testId }) => (
          <Select key={key} value={criteria[key]} onValueChange={(v) => onChange({ [key]: v })}>
            <SelectTrigger data-testid={testId} className="h-11 sm:h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}

        <div className="flex items-center gap-2">
          <Select value={criteria.sort} onValueChange={(v) => onChange({ sort: v })}>
            <SelectTrigger data-testid="admin-venues-sort" className="h-11 sm:h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VENUE_SORTS.map((o) => (
                <SelectItem key={o.value} value={o.value}>Sort: {o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* The direction toggle names the order it will produce, not the
              one it is in — a button labelled with the current state reads
              as a status and gets pressed by mistake. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 sm:h-9 shrink-0 text-xs"
            onClick={() => onChange({ direction: criteria.direction === "desc" ? "asc" : "desc" })}
            aria-label={`${sortMeta.label}, ${criteria.direction === "desc" ? "descending" : "ascending"} — press to reverse`}
            data-testid="admin-venues-sort-direction"
          >
            <SortIcon size={14} />
          </Button>
        </div>
      </div>

      {narrowed && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs text-slate-600"
          onClick={onClear}
          data-testid="admin-venues-clear-filters"
        >
          <X size={12} className="mr-1" /> Clear filters
        </Button>
      )}
    </div>
  );
}

/**
 * Issue #603 — 100 rows a page, with the controls repeated above AND below
 * the list, because a page of 100 rows is a long scroll and a pager only at
 * one end is a pager you have to hunt for.
 *
 * The sentence beside them is `describeVenuePage`, which always states the
 * range and the total together: a bare "97" under an active filter reads as
 * the size of the directory, and after the OSM load it very nearly is one.
 */
function VenuePager({ result, criteria, onPage, position }) {
  const { page, pageCount } = result;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2"
      data-testid={`admin-venues-pager-${position}`}
    >
      <p className="text-xs text-slate-500" data-testid={`admin-venues-count-${position}`}>
        {describeVenuePage(result, criteria)}
      </p>
      {pageCount > 1 && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
            data-testid={`admin-venues-prev-${position}`}
          >
            <ChevronLeft size={12} className="mr-1" /> Previous
          </Button>
          <span className="text-xs text-slate-600" data-testid={`admin-venues-page-${position}`}>
            Page {page} of {pageCount}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            disabled={page >= pageCount}
            onClick={() => onPage(page + 1)}
            data-testid={`admin-venues-next-${position}`}
          >
            Next <ChevronRight size={12} className="ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * ISSUE #612 — SELECT MANY, PUBLISH ONCE.
 *
 * Martin is hand-entering grounds from council published documents and says
 * he will keep doing it. #606 gave him a Publish button per row; at 27 venues
 * that is tedious and survivable, at council-list scale it is the thing that
 * makes a good process get abandoned. The "Private (unpublished)" filter #606
 * added is the entry point: filter to the worklist, tick the rows, press once.
 *
 * THE FOUR THINGS THIS PANEL IS ACCOUNTABLE FOR, and where each one lives:
 *
 *   ONE CONFIRM, N AUDIT ROWS. The confirm is here; the ledger is the API's,
 *   which calls `setGroundPublished` once per ground — see
 *   `api/src/routes/adminGrounds.ts`. Nothing on this screen batches a write.
 *
 *   THE CONFIRM STATES THE COUNT AND IS DISMISSABLE. Publishing is one-way
 *   (#606 built no unpublish), so the sentence is `describeBulkPublishConfirm`
 *   — the number and "there is no unpublish" — and Cancel is a plain button
 *   that writes nothing. Changing the selection while the confirm is open
 *   closes it: the batch you confirm must be the batch you armed, or the
 *   count on screen is describing a different set of venues than the one that
 *   would be published.
 *
 *   PARTIAL FAILURE IS PER ROW. The API answers 200 with a verdict per
 *   ground, never one status code for the batch, and this panel renders the
 *   failures BY NAME with the reason the API wrote — `summariseBulkPublish`.
 *   The ones that succeeded are not undone and are not re-offered; the ones
 *   that failed stay selected, so the retry is one press and is exactly them.
 *
 *   THE DUPLICATE WARNING IS A WARNING. `POST /publish/preflight` runs the
 *   shipped matcher over the armed selection and its answer is rendered above
 *   the buttons. It is not wired to `disabled` anywhere: the Confirm button
 *   reads `canConfirmBulkPublish(count)`, whose only input is the count. A
 *   preflight still in flight, one that found a duplicate for every venue,
 *   and one that failed outright all leave Confirm pressable — #612 says the
 *   admin is the reviewer and may legitimately publish a near-name match.
 */
function BulkPublishPanel({ selectedIds, selectableIds, onToggleAll, onClearSelection, onPublished }) {
  const [armed, setArmed] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [preflightFailed, setPreflightFailed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const count = selectedIds.length;
  const selectionKey = selectedIds.join(",");

  /** A change to the selection cancels an open confirm — see the header. */
  useEffect(() => {
    setArmed((a) => (a && a.key !== selectionKey ? null : a));
  }, [selectionKey]);

  function arm() {
    setArmed({ ids: selectedIds, key: selectionKey });
    setResult(null);
    setError("");
    setPreflight(null);
    setPreflightFailed(false);
    setChecking(true);
    api.post("/admin/grounds/publish/preflight", { ground_ids: selectedIds })
      .then((r) => setPreflight(r.data))
      .catch(() => setPreflightFailed(true))
      .finally(() => setChecking(false));
  }

  async function publish() {
    if (busy || !armed) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.post("/admin/grounds/publish", { ground_ids: armed.ids });
      const summary = summariseBulkPublish(res.data);
      setResult(summary);
      setArmed(null);
      onPublished(summary);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not publish these venues");
    } finally {
      setBusy(false);
    }
  }

  const warning = armed && (preflight || preflightFailed)
    ? summarisePreflight(preflight, { failed: preflightFailed })
    : null;
  const warningLine = warning ? describeDuplicateWarning(warning) : "";

  if (selectableIds.length === 0 && count === 0 && !result) return null;

  return (
    <Card className="border-slate-200" data-testid="admin-venues-bulk">
      <CardContent className="p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {selectableIds.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              /* `whitespace-normal h-auto py-1.5` overrides the Button
                 primitive's `whitespace-nowrap`: this is the longest label on
                 the strip and at 390px a nowrap button is the row that
                 overflows sideways — #615's failure family, on a control
                 rather than a name. It wraps instead. */
              className="text-xs whitespace-normal text-left h-auto py-1.5"
              onClick={onToggleAll}
              data-testid="admin-venues-bulk-select-all"
            >
              <CheckSquare size={12} className="mr-1 shrink-0" />
              {allVenuesSelected(new Set(selectedIds), selectableIds)
                ? `Clear these ${selectableIds.length}`
                : `Select all ${selectableIds.length} unpublished here`}
            </Button>
          )}
          <span className="text-xs text-slate-600" data-testid="admin-venues-bulk-count">
            {count} selected
          </span>
          {count > 0 && !armed && (
            <>
              <Button
                type="button"
                size="sm"
                className="text-xs"
                style={{ background: "#0B2E59" }}
                onClick={arm}
                data-testid="admin-venues-bulk-publish"
              >
                <Globe2 size={12} className="mr-1" /> Publish {count} selected
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={onClearSelection}
                data-testid="admin-venues-bulk-clear"
              >
                Clear selection
              </Button>
            </>
          )}
        </div>

        {armed && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2"
            data-testid="admin-venues-bulk-confirm-panel"
          >
            <p className="text-xs font-semibold text-amber-900 break-words">
              {describeBulkPublishConfirm(armed.ids.length)}
            </p>
            {checking && (
              <p
                className="inline-flex items-center gap-1 text-[11px] text-slate-600"
                data-testid="admin-venues-bulk-checking"
              >
                <Loader2 size={12} className="animate-spin" /> Checking for duplicates…
              </p>
            )}
            {warningLine && (
              <p
                className="text-[11px] text-amber-900 break-words"
                data-testid="admin-venues-bulk-duplicate-warning"
              >
                <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
                {warningLine}
              </p>
            )}
            {warning && !warning.failed && warning.withDuplicates.length > 0 && (
              <ul className="text-[11px] text-amber-900 space-y-0.5" data-testid="admin-venues-bulk-duplicate-list">
                {warning.withDuplicates.map((g) => (
                  <li key={g.ground_id} className="break-words">
                    <span className="font-semibold">{g.name}</span> — like{" "}
                    {g.duplicates.map((d) => d.name).join(", ")}
                  </li>
                ))}
              </ul>
            )}
            {warning && !warning.failed && warning.blocked.length > 0 && (
              <p className="text-[11px] text-slate-700 break-words" data-testid="admin-venues-bulk-blocked">
                {warning.blocked.length} of these cannot be published (archived, merged, or no longer in
                the directory). They will be reported and skipped; the rest still publish.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                className="text-xs"
                style={{ background: "#0B2E59" }}
                /* Reads the COUNT and `busy`. Never the preflight — #612. */
                disabled={busy || !canConfirmBulkPublish(armed.ids.length)}
                onClick={publish}
                data-testid="admin-venues-bulk-confirm"
              >
                {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
                Publish {armed.ids.length}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs"
                disabled={busy}
                onClick={() => setArmed(null)}
                data-testid="admin-venues-bulk-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 break-words" role="alert" data-testid="admin-venues-bulk-error">
            {error}
          </p>
        )}

        {result && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="admin-venues-bulk-result">
            <p className="text-xs font-semibold text-slate-800">{describeBulkPublishResult(result)}</p>
            {/* PER ROW, NOT "something went wrong" — #612's own words. Every
                failure carries the venue's name and the API's reason, and the
                successes above it are not undone. */}
            {result.failures.length > 0 && (
              <ul className="mt-1.5 space-y-1" data-testid="admin-venues-bulk-failures">
                {result.failures.map((f) => (
                  <li key={f.ground_id} className="text-[11px] text-red-700 break-words">
                    <span className="font-semibold">{f.name}</span> — {f.detail}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminVenues() {
  // #606. The publish affordance rides `canPublishGround`, the shared
  // predicate, rather than being assumed from the router's admin gate —
  // same rule the edit affordance follows, so venue authority widens in one
  // place (#158) rather than in each page.
  const { user } = useAuth();
  const [grounds, setGrounds] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Issue #603. ONE criteria object rather than nine `useState`s, because
  // the page-1 reset is a rule ABOUT the set — "any change to what is being
  // looked for resets the page" — and a rule about a set cannot be enforced
  // by nine independent setters. `applyVenueCriteria` owns it, and
  // `adminVenues.test.js` asserts it.
  const [criteria, setCriteria] = useState(DEFAULT_VENUE_CRITERIA);
  /**
   * Issue #613 — THE ROWS PUBLISHED IN THIS SITTING, kept on screen.
   *
   * Publishing changes a row's provenance, so a venue published from the
   * "Private (unpublished)" worklist stops matching the filter it was found
   * by. The obvious behaviour — it vanishes on the reload — takes the row
   * out from under the finger that just pressed Publish, shortens the list
   * mid-scroll, and leaves no evidence the write landed. Martin is working
   * 27 of these in a column on a phone.
   *
   * So a published row STAYS, in its own sorted position, wearing its new
   * state (the amber "Private" badge gone, "Just published" in its place),
   * until the next change to what is being LOOKED FOR. `selectVenues` takes
   * these ids as `keepIds`; any search, filter or sort change drops them,
   * and a page change does not — the same "what is being looked for" line
   * `applyVenueCriteria` already draws for the page-1 reset (#603). That is
   * what makes "until the next filter change" a rule rather than an
   * accident: the next list an admin asks for is the true one.
   */
  const [keptIds, setKeptIds] = useState(() => new Set());
  /**
   * Issue #612 — THE ROWS TICKED FOR A BATCH PUBLISH.
   *
   * Cleared by ANY change to what is being looked for, INCLUDING a page
   * change — which is where this rule deliberately parts company with
   * `keptIds` above. A kept row is one you just acted on and can still see; a
   * selection is a promise about what a confirm will publish, and publishing
   * is one-way. "Publish 40" must never mean forty venues, thirty of which
   * left the screen two filters ago and cannot be checked before the press.
   * `pruneVenueSelection` is the second half of the same rule, for the
   * changes the reader did not make: a reload, or a row published by the
   * per-row button while it was ticked.
   */
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const updateCriteria = useCallback((patch) => {
    // `applyVenueCriteria`'s own page-only test, so "keeps the kept rows"
    // and "keeps the page" can never come to mean two different patches.
    const keys = Object.keys(patch || {});
    const pageOnly = keys.length > 0 && keys.every((k) => k === "page");
    if (!pageOnly) setKeptIds(new Set());
    // #612: the selection goes on EVERY change, page included.
    setSelectedIds(new Set());
    setCriteria((c) => applyVenueCriteria(c, patch));
  }, []);
  const goToPage = useCallback((page) => updateCriteria({ page }), [updateCriteria]);
  const clearFilters = useCallback(() => {
    setKeptIds(new Set());
    setSelectedIds(new Set());
    setCriteria((c) => clearVenueFilters(c));
  }, []);
  const [managingId, setManagingId] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditFailed, setAuditFailed] = useState(false);

  const retry = useCallback(() => {
    setLoaded(false);
    setLoadFailed(false);
    setReloadKey((k) => k + 1);
  }, []);

  /** Every write on this page refetches rather than splicing local arrays —
   * the ledger, the archived flags and the facility rows are all
   * server-owned state, and `AdminGroundDuplicates` states the reason: a
   * client-side copy of the rule is the copy that drifts. */
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  /** A publish is a reload PLUS the promise that the row it changed stays
   * on screen (#613). Both publish affordances — the row's and the detail
   * card's — go through this one handler, so where you pressed Publish from
   * cannot change what the list does afterwards. */
  const handlePublished = useCallback((groundId) => {
    setKeptIds((prev) => (prev.has(groundId) ? prev : new Set(prev).add(groundId)));
    reload();
  }, [reload]);

  /**
   * Issue #612 — the batch landed. Three things follow, and each is the
   * counterpart of a rule stated elsewhere:
   *
   *   every PUBLISHED row is kept on screen, through #613's own mechanism, so
   *   a worklist filtered to "Private (unpublished)" does not empty itself
   *   under the finger that pressed Confirm;
   *
   *   the selection becomes exactly the rows that FAILED, so the retry is one
   *   press and cannot silently re-publish the ones that already landed;
   *
   *   the directory is refetched, because publish state is server-owned and a
   *   client-side splice is the copy that drifts.
   */
  const handleBulkPublished = useCallback((summary) => {
    setKeptIds((prev) => {
      const next = new Set(prev);
      for (const id of summary.publishedIds) next.add(id);
      return next;
    });
    setSelectedIds(new Set(summary.failedIds));
    reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    // The map page's exact pair of reads (FAC-11's `include_archived=1`
    // opt-in): the whole visible directory, and every facility at every
    // visible ground in one query rather than one per venue.
    Promise.all([
      api.get("/grounds?include_archived=1"),
      api.get("/grounds/facilities?include_archived=1"),
    ])
      .then(([g, f]) => {
        if (cancelled) return;
        // Issue #690 — this read is capped now (`GROUNDS_BBOX_MAX_ROWS`) and
        // its truncation is NOT surfaced on this page: `readViewportGrounds`
        // unwraps the rows so the console keeps working, but the ledger
        // below (`total: all.length` in `lib/adminVenues.js`) would silently
        // describe a partial directory as complete once the world directory
        // outgrows the cap. Left unaddressed here deliberately — see this
        // issue's report for why, and the residual risk it names.
        setGrounds(readViewportGrounds(g.data).grounds);
        setFacilities(f.data || []);
        setLoadFailed(false);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [reloadKey]);

  // The ledger, for the venue being managed. Refetched on every reload so a
  // save made above is visible in the trail below without a page refresh —
  // the write and its evidence on one screen.
  useEffect(() => {
    if (!managingId) { setAudit(null); setAuditFailed(false); return undefined; }
    let cancelled = false;
    setAuditFailed(false);
    api.get(`/admin/grounds/${managingId}/audit`)
      .then((r) => { if (!cancelled) setAudit(r.data?.entries || []); })
      .catch(() => { if (!cancelled) { setAudit(null); setAuditFailed(true); } });
    return () => { cancelled = true; };
  }, [managingId, reloadKey]);

  const listing = useMemo(
    () => selectVenues(grounds, criteria, { keepIds: keptIds }),
    [grounds, criteria, keptIds],
  );
  // #612. One predicate for the checkbox, the row's Publish button and the
  // batch — `canPublishGround`, never a re-derived copy (#606's rule).
  const canPublish = useCallback((g) => canPublishGround(g, user), [user]);
  const selectableIds = useMemo(
    () => selectableVenueIds(listing.rows, canPublish),
    [listing.rows, canPublish],
  );
  // The belt for changes the reader did not make: a reload that published a
  // ticked row, or removed it. Returns the same Set when nothing moved, so
  // this is not a render loop.
  useEffect(() => {
    setSelectedIds((prev) => pruneVenueSelection(prev, grounds, canPublish));
  }, [grounds, canPublish]);
  const selectedVisible = useMemo(
    () => selectableIds.filter((id) => selectedIds.has(id)),
    [selectableIds, selectedIds],
  );
  const byGround = useMemo(
    () => facilitiesByGround(facilities, new Set(grounds.map((g) => g.ground_id))),
    [facilities, grounds],
  );
  const selected = useMemo(
    () => grounds.find((g) => g.ground_id === managingId) || null,
    [grounds, managingId],
  );

  /**
   * Issue #613 — MANAGE MUST NOT LOOK INERT.
   *
   * The detail card is a sibling of the row list, not a child of the row,
   * so it renders after every row on the page — on a phone that is
   * thousands of pixels below the button that opened it, and the only
   * feedback was the label changing to "Managing". Selecting a row now
   * takes the reader to what selecting it produced. This is independent of
   * publish: it fixes Manage for editing, re-rating and the audit trail
   * too, and it is the fix that keeps working if the card ever moves.
   *
   * Guarded on the function existing because jsdom does not implement
   * `scrollIntoView`, and a page that throws in the test environment is a
   * page nothing can mount later.
   */
  const detailRef = useRef(null);
  useEffect(() => {
    if (!managingId) return;
    const el = detailRef.current;
    if (!el || typeof el.scrollIntoView !== "function") return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [managingId]);

  /**
   * Issue #616 — ARRIVING FROM A LINK, AT A LIST THAT CANNOT BE HIDING THE
   * ROW.
   *
   * `/admin/venues?ground=<id>` is how the duplicate report opens either
   * half of a pair (`venueRecordPath`, one builder shared with this
   * reader). Martin's defect was that a report naming two grounds left him
   * to find them by typing a name in here, where the Provenance filter he
   * had been living in for two days — "Private (unpublished)", #606 —
   * guarantees a nil result for every ground on that report, because the
   * report only lists PUBLIC ones. Nothing on this page says a filter is
   * why the row is missing.
   *
   * So a followed link WIDENS the list rather than searching it:
   * `focusVenueCriteria` clears the search box and every filter, sets
   * Status to "All" (clearing to the DEFAULT would leave "Active", which
   * hides an archived venue), and lands on the page the row is actually on.
   * Then it opens the record, which scrolls the detail card into view
   * through the effect above — the same behaviour pressing Manage gives.
   *
   * IT RUNS ONCE PER LINK, not once per render: `focusedRef` holds the id
   * already honoured, so an admin who then changes a filter or closes the
   * record is not fighting an effect that re-clears their list on every
   * keystroke while `?ground=` is still in the URL.
   */
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get(VENUE_FOCUS_PARAM);
  const focusedRef = useRef(null);
  useEffect(() => {
    if (!focusId || !loaded || loadFailed) return;
    if (focusedRef.current === focusId) return;
    focusedRef.current = focusId;
    setKeptIds(new Set());
    setSelectedIds(new Set());
    setCriteria((c) => focusVenueCriteria(c, grounds, focusId));
    setManagingId(focusId);
  }, [focusId, loaded, loadFailed, grounds]);

  /** A link to a venue this directory does not hold — merged away, deleted,
   * or an id typed by hand. Said out loud: a cleared list with no row on it
   * reads as a bug in the link, and this is the one thing the page knows
   * that the reader does not. */
  const focusMissing =
    Boolean(focusId) && loaded && !loadFailed && !grounds.some((g) => g.ground_id === focusId);

  return (
    <div className="space-y-6" data-testid="admin-venues-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Admin · Venues</p>
        <h1 className="font-display font-black text-2xl text-slate-900 mt-1">Venues</h1>
        <p className="text-slate-500 text-sm mt-1">
          Edit venue records, archive retired ones, manage their surfaces, and read each venue's change
          history. The list shows active venues by default — switch Status to Archived (or All) to find
          one and restore it. A venue you added that nobody else can see is badged
          “{PRIVATE_GROUND_LABEL}”; publish it to the shared directory from the row itself, or
          filter Provenance by “Private (unpublished)” to find them all.
        </p>
      </div>

      {/* Issue #1532 — adding ONE ground, by pinning it on the map. The pin
          lives on the directory map (click to drop it; an admin's add
          publishes on create, #606). #1151 trimmed Grounds out of the admin
          menu on the strength of a comment saying the add lives here, and it
          did not, so Martin was left with the bulk import alone. */}
      <Card className="border-slate-200" data-testid="admin-venue-add">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
              <MapPinPlus size={16} /> Add a single ground
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              Open the map and click the spot to pin it. A ground you pin there goes straight into the shared
              directory.
            </p>
          </div>
          <Button asChild style={{ background: "#0B2E59", minHeight: 44 }}>
            <Link to="/grounds-map" data-testid="admin-venues-add-ground">
              <MapPinPlus size={14} className="mr-2" /> Pin it on the map
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Issue #580 — the bulk importer. On a successful confirm it reloads
          the directory below so the new venues appear without a refresh. */}
      <ImportPanel onImported={reload} />

      {loaded && loadFailed && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <LoadFailureNote what="the venue directory" onRetry={retry} testId="admin-venues-load-failed" />
          </CardContent>
        </Card>
      )}

      {loaded && !loadFailed && (
        <>
          {focusMissing && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 text-sm text-amber-900 break-words" data-testid="admin-venues-focus-missing">
                That link points at a venue this directory no longer holds — it may have been merged into
                another record, or deleted. The full list is below.
              </CardContent>
            </Card>
          )}

          <VenueFilters criteria={criteria} onChange={updateCriteria} onClear={clearFilters} />

          {/* Two empty states, never one: an empty DIRECTORY and an empty
              RESULT are different facts, and only the second one is
              something the reader can undo. `listing.total` is what tells
              them apart (#603). */}
          {listing.matching === 0 && (
            <div className="text-sm text-slate-500" data-testid="admin-venues-empty">
              {listing.total === 0 ? (
                <p>The directory has no venues yet.</p>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <p>No venues match these filters.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={clearFilters}
                    data-testid="admin-venues-empty-clear"
                  >
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          )}

          {listing.matching > 0 && (
            <>
              {/* #612 — the batch, above the rows it is built from. */}
              <BulkPublishPanel
                selectedIds={selectedVisible}
                selectableIds={selectableIds}
                onToggleAll={() =>
                  setSelectedIds((prev) => toggleAllVenueSelection(prev, selectableIds))
                }
                onClearSelection={clearSelection}
                onPublished={handleBulkPublished}
              />

              <VenuePager result={listing} criteria={criteria} onPage={goToPage} position="top" />

              <div className="space-y-2">
                {listing.rows.map((g) => (
                  <VenueRow
                    key={g.ground_id}
                    g={g}
                    active={g.ground_id === managingId}
                    onManage={() => setManagingId(g.ground_id === managingId ? null : g.ground_id)}
                    canPublish={canPublish(g)}
                    justPublished={keptIds.has(g.ground_id) && !isPrivateGround(g)}
                    onPublished={() => handlePublished(g.ground_id)}
                    selected={selectedIds.has(g.ground_id)}
                    onToggleSelected={() =>
                      setSelectedIds((prev) => toggleVenueSelection(prev, g.ground_id))
                    }
                  />
                ))}
              </div>

              <VenuePager result={listing} criteria={criteria} onPage={goToPage} position="bottom" />
            </>
          )}

          {selected && (
            <Card className="border-slate-200" data-testid="admin-venue-detail" ref={detailRef}>
              <CardContent className="p-5">
                <h2 className="font-display font-bold text-lg text-slate-900 break-words">{selected.name}</h2>
                {/* #606 — above the edit form, because "nobody else can see
                    this" is the first thing to know about the record you
                    just opened. */}
                {canPublishGround(selected, user) && (
                  <PublishControl
                    ground={selected}
                    onPublished={() => handlePublished(selected.ground_id)}
                  />
                )}
                {/* The venue's own rating, one tap — the same gesture and
                    component FAC-09 gave the creator (`ConditionUpdate`),
                    admin-gated by this page's router entry and the API. */}
                <ConditionUpdate
                  target={{ groundId: selected.ground_id }}
                  condition={selected.condition}
                  ratedAt={selected.condition_rated_at}
                  canEdit
                  label="Condition"
                  testId={`admin-venue-condition-${selected.ground_id}`}
                  onUpdated={reload}
                />
                {/* The creator's exact form — reused, not forked (#497).
                    `GroundEditPanel` carries the ground's `ArchiveToggle`
                    inside it, so edit and archive arrive together, which is
                    Q4's sentence ("edit a venue and archive a venue from
                    the admin surface"). No map here, so `pin` stays unset
                    and the coordinate fields are the input — the panel is
                    built for that (#379 kept them for exactly the surface
                    with no map). */}
                <GroundEditPanel
                  key={selected.ground_id}
                  ground={selected}
                  onSaved={reload}
                  onCancel={() => setManagingId(null)}
                />

                <div className="mt-5 border-t border-slate-200 pt-4">
                  <h3 className="font-display font-bold text-sm text-slate-900">Surfaces &amp; nets</h3>
                  <AdminFacilityRows ground={selected} list={byGround.get(selected.ground_id)} onChanged={reload} />
                </div>

                <AuditTrail entries={audit} failed={auditFailed} onRetry={reload} />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
